import React, { useState, useEffect, useContext, useMemo } from "react";

import { useLocation } from "react-router-dom";
import { AuthContext } from "../components/contexts/AuthContext";

import { useTranslation } from "react-i18next";

import { useHVSubstationsWithCompanyAndSite } from "../components/data/Assets/useHVSubstationsWithCompanyAndSite";

import Box from "@mui/material/Box";

import { MessageToast, ToastMode } from "../components/shared/MessageToast";
import { Customer } from "../components/shared/interfaces/Customer";
import { CustomerDropdown } from "../components/CustomerDropdown";
import { Snackbar } from "../components/shared/Snackbar";
import { HvSubstationGrid } from "../components/hvSubstations/HvSubstationGrid";
import { HvSubstationCreate } from "../components/hvSubstations/HvSubstationCreate";
import { convertAssetToHvSubstation } from "../components/shared/helpers/HvSubstationHelpers";
import RouteBreadcrumbs from "../components/shared/RouteBreadcrumbs";
import { PersistedCompanyDropdown } from "../components/PersistedCompanyDropdown";
import { useCompaniesByCustomerId } from "../components/data/useCompaniesByCustomerId";
import { useSitesByCompanyId } from "../components/data/useSitesByCompanyId";
import { PersistedSiteDropdown } from "../components/PersistedSiteDropdown";
import { Stack } from "@mui/material";

interface LocationState {
  deletedHvSubstationName?: string;
}

export function HvSubstations() {
  const { t } = useTranslation();
  const authContext = useContext(AuthContext);
  const location = useLocation();
  const locationState = location.state as LocationState;

  const [errorToast, setErrorToast] = useState({
    show: false,
    mode: ToastMode.Error,
    text: "",
  });
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarText, setSnackbarText] = useState("");

  const [selectedCustomer, setSelectedCustomer] = useState<Customer | undefined>();
  const [selectedCompanyName, setSelectedCompanyName] = useState<string | undefined>();
  const [selectedSiteName, setSelectedSiteName] = useState<string | undefined>();

  const companies = useCompaniesByCustomerId(selectedCustomer?.id);

  const companyId = useMemo(() => {
    if (companies.data && selectedCompanyName) {
      return companies.data?.find((c) => c.name === selectedCompanyName)?.id;
    }
  }, [companies.data, selectedCompanyName]);

  const sites = useSitesByCompanyId(companyId);

  const siteId = useMemo(() => {
    if (sites.data && selectedSiteName) {
      return sites.data?.find((c) => c.name === selectedSiteName)?.id;
    }
  }, [sites.data, selectedSiteName]);

  const { refetch: refetchHvSubstations, ...hvSubstations } = useHVSubstationsWithCompanyAndSite(
    selectedCustomer && selectedCustomer.id,
    companyId,
    siteId,
  );

  const [hvSubstationCreatePanelOpen, setHvSubstationCreatePanelOpen] = useState(false);

  useEffect(() => {
    if (authContext.userIsAuthenticated) {
      refetchHvSubstations();
    }
  }, [authContext.userIsAuthenticated, refetchHvSubstations]);

  useEffect(() => {
    if (hvSubstations.error) {
      setErrorToast({
        text: t("common.dataFetchError", {
          val: "$t(substations.hvSubstations)",
        }),
        mode: ToastMode.Error,
        show: true,
      });
    } else {
      setErrorToast({
        text: "",
        mode: ToastMode.Error,
        show: false,
      });
    }
  }, [t, hvSubstations.error]);

  useEffect(() => {
    if (locationState?.deletedHvSubstationName) {
      setSnackbarText(
        t("substations.substationDeleted", {
          val: locationState.deletedHvSubstationName,
        }),
      );
      setShowSnackbar(true);

      //Clear the deleted HV Substation Asset ID from location state so that the success snackbar only appears once
      window.history.replaceState({ deletedHvSubstationName: undefined }, document.title);
    }
  }, [locationState?.deletedHvSubstationName, t]);

  function handleCustomerSelect(customer?: Customer) {
    setSelectedCustomer(customer);
  }

  function handleCompanyChange(selectedCompanyName: string) {
    setSelectedCompanyName(selectedCompanyName);
  }

  function handleSiteChange(siteName: string) {
    setSelectedSiteName(siteName);
  }

  function handleNewHvSubstationButtonClick() {
    setHvSubstationCreatePanelOpen(true);
  }

  function handleNewHvSubstationPanelClose() {
    setHvSubstationCreatePanelOpen(false);
  }

  function handleHvSubstationCreated(substationId: number, substationName: string) {
    refetchHvSubstations();
    setSnackbarText(
      t("common.dataSuccessfullyCreated", {
        entity: t("substations.hvSubstation"),
        id: substationName || substationId,
      }),
    );
    setShowSnackbar(true);
    setHvSubstationCreatePanelOpen(false);
  }

  return (
    <React.Fragment>
      <Box marginBottom={2}>
        <RouteBreadcrumbs />
      </Box>

      <Stack direction="row">
        <CustomerDropdown
          selectedCustomer={selectedCustomer}
          onCustomerSelect={handleCustomerSelect}
          onSingleCustomerAvailable={(customer) => setSelectedCustomer(customer)}
          showLabel
        />
        <PersistedCompanyDropdown customerId={selectedCustomer?.id} onCompanyChange={handleCompanyChange} showLabel />
        <PersistedSiteDropdown
          companyId={companyId}
          customerId={selectedCustomer?.id}
          onSiteChange={handleSiteChange}
          showLabel
        />
      </Stack>

      {errorToast.show ? (
        <div className="w-100 d-flex justify-content-center pt-2">
          <MessageToast text={errorToast.text} mode={errorToast.mode} show={errorToast.show}></MessageToast>
        </div>
      ) : (
        selectedCustomer && (
          <Box marginTop={2}>
            <HvSubstationGrid
              customerSelected={selectedCustomer != null}
              hvSubstationData={hvSubstations.data}
              hvSubstationDataLoading={hvSubstations.isLoading}
              hvSubstationDataFetching={hvSubstations.isFetching}
              onDataRefreshClick={refetchHvSubstations}
              onNewHvSubstationClick={handleNewHvSubstationButtonClick}
            />

            {hvSubstationCreatePanelOpen && hvSubstations.data && (
              <HvSubstationCreate
                open={hvSubstationCreatePanelOpen}
                onClose={handleNewHvSubstationPanelClose}
                customerId={selectedCustomer.id}
                onHvSubstationCreated={handleHvSubstationCreated}
                existingSubstations={hvSubstations.data?.map((substation) => convertAssetToHvSubstation(substation))}
                refreshSubstationData={refetchHvSubstations}
              />
            )}
          </Box>
        )
      )}

      <Snackbar
        open={showSnackbar}
        onClose={() => {
          setShowSnackbar(false);
        }}
        message={snackbarText}
      />
    </React.Fragment>
  );
}
